import { createRouter, createWebHistory } from 'vue-router';

import { forceLightTheme } from '@/composables/useTheme.js';
import { useAuthStore } from '@/stores/auth.js';

/**
 * Роутинг Ф0.
 *
 * `bare: true` — страница рисуется без шелла (вход, колбэк, 404 без сессии).
 * Публичная страница мероприятия и регистрация по ссылке появятся в Ф4,
 * админка мероприятия — в Ф2; структура путей заложена так, чтобы их можно
 * было довесить, не переписывая существующие.
 */
export const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
  routes: [
    {
      path: '/',
      name: 'events',
      component: () => import('@/pages/EventsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/events/new',
      name: 'event-create',
      component: () => import('@/pages/EventFormPage.vue'),
      meta: { requiresAuth: true },
    },
    // Мероприятие: оболочка с меню, разделы — вложенные маршруты. У каждого
    // свой адрес, иначе нельзя ни дать ссылку коллеге, ни вернуться «назад».
    {
      path: '/events/:id',
      component: () => import('@/pages/EventLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        { path: '', name: 'event', redirect: { name: 'event-overview' } },
        {
          path: 'overview',
          name: 'event-overview',
          component: () => import('@/pages/event/OverviewSection.vue'),
        },
        {
          path: 'applications',
          name: 'event-applications',
          component: () => import('@/pages/event/ApplicationsSection.vue'),
        },
        {
          path: 'participants',
          name: 'event-participants',
          component: () => import('@/pages/event/ParticipantsSection.vue'),
        },
        {
          path: 'organizations',
          name: 'event-organizations',
          component: () => import('@/pages/event/OrganizationsSection.vue'),
        },
        {
          path: 'letters',
          name: 'event-letters',
          component: () => import('@/pages/event/LettersReceivedSection.vue'),
        },
        {
          path: 'roles',
          name: 'event-roles',
          component: () => import('@/pages/event/RolesSection.vue'),
        },
        {
          path: 'program',
          name: 'event-program',
          component: () => import('@/pages/event/ProgramSection.vue'),
        },
        {
          path: 'venue',
          name: 'event-venue',
          component: () => import('@/pages/event/VenueSection.vue'),
        },
        {
          path: 'accreditation',
          name: 'event-accreditation',
          component: () => import('@/pages/event/AccreditationSection.vue'),
        },
        {
          path: 'announcements',
          name: 'event-announcements',
          component: () => import('@/pages/event/AnnouncementsSection.vue'),
        },
        {
          path: 'settings',
          name: 'event-settings',
          component: () => import('@/pages/event/SettingsSection.vue'),
        },
      ],
    },
    {
      path: '/events/:id/forms/:formId',
      name: 'form-constructor',
      component: () => import('@/pages/FormConstructorPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/events/:id/edit',
      name: 'event-edit',
      component: () => import('@/pages/EventFormPage.vue'),
      meta: { requiresAuth: true },
    },
    // Публичные маршруты: страница мероприятия и регистрация по ссылке
    // открываются без входа — иначе человек не поймёт, куда его зовут.
    {
      path: '/program/:slug',
      name: 'public-program',
      component: () => import('@/pages/ProgramPublicPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/e/:slug',
      name: 'public-event',
      component: () => import('@/pages/PublicEventPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/join/:token',
      name: 'join',
      component: () => import('@/pages/JoinPage.vue'),
      meta: { bare: true },
    },
    // Приглашение делегата. Вход НЕ требуется: приходит представитель
    // компании, которого в системе, как правило, ещё нет вовсе — аккаунт он
    // заводит прямо здесь.
    {
      path: '/delegate/:token',
      name: 'delegate-invite',
      component: () => import('@/pages/DelegateInvitePage.vue'),
      meta: { bare: true },
    },
    // Установка пароля по ссылке из письма. Вход не требуется по определению:
    // сюда приходят именно потому, что войти не получается.
    {
      path: '/set-password',
      name: 'set-password',
      component: () => import('@/pages/SetPasswordPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/my',
      name: 'my',
      component: () => import('@/pages/MyParticipationsPage.vue'),
      meta: { requiresAuth: true },
    },
    // Один адрес на все роли: его печатают в бумажном письме организациям и
    // диктуют по телефону. Что показать — участнику или регистратору —
    // решает учётная запись, поэтому ни мероприятия, ни организации в пути.
    {
      path: '/letter',
      name: 'letter',
      component: () => import('@/pages/LetterPage.vue'),
      meta: { requiresAuth: true },
    },
    // Администраторы платформы: право шире любой роли мероприятия, поэтому
    // экран не внутри мероприятия, а рядом со списком.
    {
      path: '/platform-admins',
      name: 'platform-admins',
      component: () => import('@/pages/PlatformAdminsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/notifications',
      name: 'notifications',
      component: () => import('@/pages/NotificationsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/my/:eventId/schedule',
      name: 'schedule',
      component: () => import('@/pages/SchedulePage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/LoginPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/pages/AuthCallbackPage.vue'),
      meta: { bare: true },
    },
    {
      path: '/access-denied',
      name: 'access-denied',
      component: () => import('@/pages/AccessDeniedPage.vue'),
      meta: { requiresAuth: true, bare: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/NotFoundPage.vue'),
      meta: { bare: true },
    },
  ],
});

/**
 * Хвост из знаков после читаемого кода ссылки: «…-fg7ptz-», «…-fg7ptz,».
 *
 * Ссылки уходят в письмах Word, и при переносе строки или копировании к ним
 * прилипают дефис, запятая или скобка — человек видел «ссылка не найдена».
 * Трогаем только читаемые коды (`org-rol-abc123`): у старых случайных кодов
 * дефис на конце бывает законным.
 */
const READABLE_WITH_TAIL = /^([a-z0-9]+(?:-[a-z0-9]+)*-[a-z0-9]{6})[-.,;:!?)»"'’]+$/;

router.beforeEach((to) => {
  const auth = useAuthStore();

  if ((to.name === 'join' || to.name === 'delegate-invite') && typeof to.params.token === 'string') {
    const clean = READABLE_WITH_TAIL.exec(to.params.token)?.[1];
    if (clean) return { name: to.name, params: { token: clean }, query: to.query, replace: true };
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login', query: { returnTo: to.fullPath } };
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'events' };
  }
  // Залогинен, но ни админ платформы, ни оргкоманда, ни делегат.
  //
  // Исключения двух сортов. Приглашение делегата: туда человек приходит
  // именно потому, что прав у него ещё нет, и получает их там. И личные
  // страницы — свои заявки, письмо организации, своё расписание: они для
  // того и существуют, чтобы их открывал участник, которому в оргкоманде
  // делать нечего. Бэкенд на этих маршрутах и так отдаёт только его
  // собственные данные.
  const OPEN_TO_ANYONE = new Set([
    'access-denied',
    'delegate-invite',
    'my',
    'letter',
    'schedule',
  ]);
  if (
    to.meta.requiresAuth &&
    auth.isAuthenticated &&
    !auth.hasAccess &&
    typeof to.name === 'string' &&
    !OPEN_TO_ANYONE.has(to.name)
  ) {
    // Ведём к своим заявкам, а не к «попросите добавить вас в оргкоманду».
    // Человек, зарегистрировавшийся на мероприятие, в оргкоманду и не
    // собирался: для него это сообщение было просто неверным, и дальше него
    // он не проходил. Если заявок нет, страница скажет об этом сама.
    return { name: 'my' };
  }
  return true;
});

/**
 * Публичные страницы держим светлыми.
 *
 * Тема — настройка рабочего места оргкоманды, а `bare`-страницы открывает
 * человек со стороны, у которого этой настройки нет и быть не может: он видит
 * бланк заявки, и его цвет не должен зависеть от того, что стоит у него в
 * системе.
 */
router.afterEach((to) => {
  forceLightTheme(to.meta.bare === true);
});
